import React, { useEffect } from "react";
import LogRocket from "logrocket";
import styles from "./App.module.scss";
import { useMst } from "./store/root";
import ExtensionPanel from "./views/ExtensionPanel";
import { CSInterface } from "csinterface-ts";
import {
  renderCompleteListener,
  renderImageProcessListener,
  renderFontsListener,
  alertListener,
  cancelRenderListener,
  renderUpdate,
  renderStart,
  debug,
} from "./jsxProviders";

import DevPanel from "./components/DevPanel";
import { CreateProject } from "./components/modals/CreateProject";
import { SceneHasData } from "./components/modals/SceneHasData";
import { AlertModal } from "./components/modals/Alert";
import { NewerVersionModal } from "./components/modals/NewerVersion";
import { ProblemLayers } from "./components/modals/ProblemLayers";
import { FileSizeTooBig } from "./components/modals/FileSizeTooBig";
import { MainButton } from "./components/baseComponents/MainButton/MainButton";
import lottie, { AnimationItem } from "lottie-web";
import selectProjectAnimation from "./assets/animations/foxIdle.json";
import { alertForNewVersion } from "./alertForNewVersion";
import HomePage from "./views/HomePage/HomePage";
import Footer from "./views/Footer";
import { observer } from "mobx-react-lite";
import { analyticsInstance } from "./analytics/analyticsCtrls";
import { environment } from "./store/utils/consts";
import { WarningModal } from "./components/modals/WarningModal";
import { useEnvironment } from "./hooks/useEnvironment";
import { EnvironmentInfo, EnvironmentDebugPanel } from "./components/EnvironmentInfo";

const { csInterface }: { csInterface: CSInterface } = window as any;

export const CsInterfaceContext = React.createContext(csInterface);


function addJsxListeners() {
  renderCompleteListener();
  renderImageProcessListener();
  renderFontsListener();
  alertListener();
  cancelRenderListener();
  renderUpdate();
  renderStart();
  
  // Use REACT_APP_HOST_TYPE for JSX debug since we need it at startup
  if (process.env.REACT_APP_HOST_TYPE === "development" || process.env.REACT_APP_HOST_TYPE === "local") {
    debug();
  }
}

export const App = observer(() => {
  const {
    projectStore: { loadProjects, selectedProject, getAEVersion },
    compositionStore: { listAllComps },
    authStore: { userName, userEmail, signOut, setNewSession, loadAccountData },
    uiStore: { setNewerVersion, setNewerVersionModalVisible },
  } = useMst();

  const envInfo = useEnvironment();

  // Use the detected environment instead of hardcoded one
  console.log("Environment Info:", envInfo);
  console.log("Is Development:", envInfo.isDevelopment);
  const currentEnvironment = envInfo.isDevelopment ? "development" : "production";

  useEffect(() => {
    // Load account data first, then load projects (which needs the account ID)
    (async () => {
      await loadAccountData();
      await loadProjects();
      addJsxListeners();
      listAllComps();

      // Log environment info
      console.log("App initialized with environment:", envInfo.environment);
      console.log("Environment config:", envInfo.config);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  useEffect(() => {
    window.onfocus = (e) => {
      listAllComps();
    };
  }, [listAllComps]);
  
  useEffect(() => {
    if (currentEnvironment === "production") {
      LogRocket.init("tphrpd/blings-ae-extension");
      LogRocket.identify("USER_IN_APP_ID", {
        name: userName || "",
        email: userEmail,
      });
    }
  }, [currentEnvironment, userName, userEmail]);
  useEffect(() => {
    let selectProjectPlay: AnimationItem;
    const processContainer = document.querySelector("#no-connection");
    if (processContainer) {
      selectProjectPlay = lottie.loadAnimation({
        container: processContainer,
        renderer: "svg",
        loop: false,
        autoplay: true,
        animationData: selectProjectAnimation,
      });
    }
    return () => {
      selectProjectPlay?.destroy();
    };
  }, []);
  useEffect(() => {
    const visited = sessionStorage.getItem("visited");

    // If 'visited' key doesn't exist, it's the user's first time in this session
    if (!visited) {
      setNewSession(true);
      sessionStorage.setItem("visited", "true");
    } else {
      setNewSession(false);
    }
  }, [selectedProject]); //re-run if you click on a project

  useEffect(() => {
    alertForNewVersion(setNewerVersionModalVisible, setNewerVersion);
  });
  useEffect(() => {
    // @ts-ignore
    lottie.setIDPrefix("onStartAnimation"); //react-lottie-web overwrites the same ids. this fixes the issue
    const sendAnalytics = async () => {
      await getAEVersion();
      analyticsInstance.sendAnalytics("extensionLoaded");
    };
    sendAnalytics();
  }, [getAEVersion]);
  return (
    <CsInterfaceContext.Provider value={csInterface}>
      <div className={styles.App}>
        {/* {environment === "development" && (
          <div className={styles.Dev}>
            <p>Developers Version</p>
          </div>
        )} */}
        {!window.navigator.onLine ? (
          <div className={styles.NoInternet}>
            <div id="no-connection"></div>
            <p className={styles.Speech}>
              Oh Oh, something happened. Try again in few seconds
            </p>
            <div style={{ marginTop: "1rem" }}>
              <MainButton
                onClick={() => window.location.reload()}
                text="Refresh"
              />
            </div>
          </div>
        ) : selectedProject ? (
          <ExtensionPanel environment={currentEnvironment} />
        ) : (
          <HomePage environment={currentEnvironment} />
        )}
        <Footer environment={currentEnvironment} signOut={signOut} />
        
        {/* Environment indicators */}
        <EnvironmentInfo />
        <EnvironmentDebugPanel />
        
        {/* {process.env.REACT_APP_HOST_TYPE === "development" && <DevPanel />} */}
        {/* Modals */}  
        <CreateProject />
        <SceneHasData />
        <AlertModal />
        <NewerVersionModal />
        <ProblemLayers />
        <FileSizeTooBig />
        <WarningModal/>
      </div>
    </CsInterfaceContext.Provider>
  );
});
